import React, { useState } from 'react';
import TextInput from './TextInput';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { GroupBuyRequest, IConfig, TPayment } from '../config/interfaces';
import { CancelIcon } from './CancelIcon';
import { getCobuyerLabel, getPrimaryBuyerLabel, isPropertyRental } from '../config/constants';

interface BuyerFormProps {
	payments: TPayment[];
	updatePayment: (index: number, field: keyof TPayment, value: string) => void;
	addCoBuyer: () => void;
	removePayment: (index: number) => void;
	saveAndCollapseForm: (index: number) => void;
	expandForm: (index: number) => void;
	collapsedForms: Set<number>;
	settings: GroupBuyRequest;
	color?: string;
	defaultPhoneCode?: string;
	includeQuantity: boolean;
	errors: Record<number, Partial<TPayment>>;
	config: IConfig;
	orderAmount: number;
	totalPaymentAmount: number;
}
export const BuyerForm = ({
	payments,
	updatePayment,
	addCoBuyer,
	removePayment,
	saveAndCollapseForm,
	expandForm,
	collapsedForms,
	settings,
	color,
	defaultPhoneCode,
	includeQuantity,
	errors,
	config,
	orderAmount,
	totalPaymentAmount,
}: BuyerFormProps) => {
	const customerConfig = settings.customer_details_config;
	const [showInfoBlock, setShowInfoBlock] = useState(true);

	const primaryLabel = getPrimaryBuyerLabel(config);
	const isRental = isPropertyRental(config);

	const renderCollapsedForm = (payment: TPayment, index: number) => {
		const paymentErrors = errors[index] || {};
		const hasErrors = Object.keys(paymentErrors).length > 0;
		
		return (
			<div key={index} className={`mb-4 p-3 border rounded-lg ${hasErrors ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50'}`}>
				<div className="flex items-center justify-between">
					<div>
						<div className="flex items-center gap-2">
							<h3 className="text-sm font-medium">{index === 0 ? primaryLabel : getCobuyerLabel(config, index)}</h3>
							{hasErrors && (
								<span className="text-red-600 text-xs bg-red-100 px-2 py-0.5 rounded-full">
									Has errors
								</span>
							)}
						</div>
						<p className="text-xs text-gray-600">
							{payment.firstName} {payment.lastName} ({payment.email})
						</p>
						{hasErrors && (
							<div className="mt-2">
								{Object.entries(paymentErrors).map(([field, error]) => (
									<p key={field} className="text-red-500 text-xs">
										{error as string}
									</p>
								))}
							</div>
						)}
					</div>
					<div className="flex gap-2">
						<button
							onClick={() => expandForm(index)}
							className={`text-xs px-2 py-1 rounded hover:bg-blue-50 ${hasErrors ? 'text-red-600 hover:text-red-800' : 'text-blue-600 hover:text-blue-800'}`}
						>
							{hasErrors ? 'Fix Errors' : 'Edit'}
						</button>
						{index > 0 && (
							<button
								onClick={() => removePayment(index)}
								className="text-red-600 hover:text-red-800 text-xs px-2 py-1 rounded hover:bg-red-50 flex items-center gap-1"
							>
								<CancelIcon className="w-5 h-5" /> Remove
							</button>
						)}
					</div>
				</div>
			</div>
		);
	};

	const canSaveForm = (payment: TPayment, index: number) => {
		// Check if required fields are filled
		const config = customerConfig;

		// First name, Last name, and Email are mandatory
		if (config.fullname && (!payment.firstName || !payment.lastName)) return false;
		if (config.email && !payment.email) return false;
		// Phone is optional - no longer required
		// if (config.phone_no && !payment.phone_no) return false;
		if (!payment.amount && payment.amount !== '0') return false;

		return true;
	};

	const renderPaymentForm = (payment: TPayment, index: number) => {
		const paymentErrors = errors[index] || {};

		// Show collapsed view if form is collapsed
		if (collapsedForms.has(index)) {
			return renderCollapsedForm(payment, index);
		}

		return (
			<div key={index} className="mb-6 px-1 border-b">
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-base font-semibold">{index === 0 ? primaryLabel : getCobuyerLabel(config, index)}</h3>
					{index > 1 && (
						<button
							onClick={() => removePayment(index)}
							className="text-red-600 hover:text-red-800 text-sm px-2 py-1 rounded hover:bg-red-50 flex items-center gap-1"
						>
							<CancelIcon className="w-5 h-5" /> Remove
						</button>
					)}
				</div>

				<div>
					{customerConfig.fullname && (
						<div className="flex items-center gap-2">
							<div className="w-full">
								<TextInput
									value={payment.firstName || ''}
									placeholder="Suzy"
									label="First name"
									color={color}
									tooltip="Enter First name"
									onChange={(value) => updatePayment(index, 'firstName', value)}
									onBlur={() => {
										// Auto-save on blur - use setTimeout to ensure state is updated
										setTimeout(() => {
											const updatedPayment = payments[index];
											if (updatedPayment && canSaveForm(updatedPayment, index)) {
												saveAndCollapseForm(index);
											}
										}, 100);
									}}
									className="w-full"
								/>
								{paymentErrors.name && <p className="text-red-500 text-xs">{paymentErrors.name}</p>}
							</div>
							<div className="w-full">
								<TextInput
									value={payment.lastName || ''}
									placeholder="Karjha"
									label="Last name"
									color={color}
									tooltip="Enter Last name"
									onChange={(value) => updatePayment(index, 'lastName', value)}
									onBlur={() => {
										// Auto-save on blur - use setTimeout to ensure state is updated
										setTimeout(() => {
											const updatedPayment = payments[index];
											if (updatedPayment && canSaveForm(updatedPayment, index)) {
												saveAndCollapseForm(index);
											}
										}, 100);
									}}
									className="w-full"
								/>
								{paymentErrors.name && <p className="text-red-500 text-xs">{paymentErrors.name}</p>}
							</div>
						</div>
					)}
				</div>
				<div className="flex gap-2">
					{customerConfig.email && (
						<div className="w-full">
							<TextInput
								value={payment.email || ''}
								label="Email"
								placeholder="name@example.com"
								color={color}
								onChange={(value) => updatePayment(index, 'email', value)}
								onBlur={() => {
									// Auto-save on blur - use setTimeout to ensure state is updated
									setTimeout(() => {
										const updatedPayment = payments[index];
										if (updatedPayment && canSaveForm(updatedPayment, index)) {
											saveAndCollapseForm(index);
										}
									}, 100);
								}}
								className="w-full"
							/>
							{paymentErrors.email && <p className="text-red-500 text-xs">{paymentErrors.email}</p>}
						</div>
					)}

					{customerConfig.phone_no && (
						<div className="w-full">
							<p className="text-sm font-medium mb-2">Phone number (optional)</p>

							<PhoneInput
								placeholder="Enter phone number"
								defaultCountry={defaultPhoneCode?.toLowerCase()}
								countrySelectorStyleProps={{
									buttonStyle: { border: 0, backgroundColor: 'rgba(249, 250, 251)' },
								}}
								inputStyle={{
									background: 'rgba(249, 250, 251)',
									border: 0,
									paddingLeft: '5px',
									width: '100%',
								}}
								className={`rounded-lg bg-gray-50 mb-4 border border-gray-300 text-gray-900 ${
									color ? `focus:ring-[${color}] focus:border-[${color}]` : 'focus:ring-blue-500 focus:border-blue-500'
								}  text-sm py-0.5 pl-1 `}
								value={payment.phone_no || ''}
								onChange={(value) => value && updatePayment(index, 'phone_no', value)}
								onBlur={() => {
									// Auto-save on blur - use setTimeout to ensure state is updated
									setTimeout(() => {
										const updatedPayment = payments[index];
										if (updatedPayment && canSaveForm(updatedPayment, index)) {
											saveAndCollapseForm(index);
										}
									}, 100);
								}}
							/>

							{paymentErrors.phone_no && <p className="text-red-500 text-xs">{paymentErrors.phone_no}</p>}
						</div>
					)}
				</div>

				<div className="flex">
					<div className="w-full">
						<TextInput
							value={payment.amount}
							label="Amount"
							color={color}
							type="number"
							tooltip="Enter part amount for this buyer to pay"
							placeholder="Enter Amount"
							onChange={(value) => updatePayment(index, 'amount', value)}
							onBlur={() => {
								// Auto-save on blur - use setTimeout to ensure state is updated
								setTimeout(() => {
									const updatedPayment = payments[index];
									if (updatedPayment && canSaveForm(updatedPayment, index)) {
										saveAndCollapseForm(index);
									}
								}, 100);
							}}
							className="w-full"
						/>
						{paymentErrors.amount && <p className="text-red-500 text-xs">{paymentErrors.amount}</p>}
					</div>

					{includeQuantity && (
						<div className="w-full ml-4">
							<TextInput
								value={payment?.quantity || ''}
								label="Quantity"
								color={color}
								tooltip="Enter the quantity you want to allocate to this buyer. If single product, Enter 1 for the cobuyr receiving this product, otherwise enter 0"
								type="number"
								placeholder="Enter Quantity"
								onChange={(value) => updatePayment(index, 'quantity', value)}
								onBlur={() => {
									// Auto-save on blur - use setTimeout to ensure state is updated
									setTimeout(() => {
										const updatedPayment = payments[index];
										if (updatedPayment && canSaveForm(updatedPayment, index)) {
											saveAndCollapseForm(index);
										}
									}, 100);
								}}
								className="w-full"
							/>
							{paymentErrors.quantity && <p className="text-red-500 text-xs">{paymentErrors.quantity}</p>}
						</div>
					)}
				</div>

				{/* Auto-save on blur - no Save button needed */}
			</div>
		);
	};

	return (
		<div>
			<style>{`
				.payment-forms-container::-webkit-scrollbar {
					display: none;
				}
			`}</style>
			{showInfoBlock && (
				<div className="bg-[#FAFAFA] px-6 py-4 rounded-[10px] mb-6">
					<div className="flex justify-between items-center">
						<p className=" text-gray-700 text-[14px]">
							{isRental 
								? `As the ${primaryLabel.toLowerCase()}, you need to enter your contact information before allocating who within your group will pay`
								: `As the primary buyer, you need to enter your contact information before allocating who within your group will pay and what products everyone will receive`
							}{' '}
						</p>
						<div className="cursor-pointer" onClick={() => setShowInfoBlock(false)}>
							<CancelIcon />
						</div>
					</div>
				</div>
			)}

			{/* Map through all payments to show their forms */}
			<div
				className="payment-forms-container max-h-[430px] overflow-y-auto"
				style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
			>
				{payments.map((payment, index) => renderPaymentForm(payment, index))}
			</div>

			{/* Add Co-Buyer button - only show if primary buyer is saved and total amount hasn't reached order amount */}
			{(() => {
				const primaryBuyerSaved = collapsedForms.has(0);
				const canAddGuest = primaryBuyerSaved && totalPaymentAmount < orderAmount;
				
				if (!canAddGuest) return null;
				
				return (
					<div className="flex items-center gap-2 mb-3 cursor-pointer" onClick={addCoBuyer}>
						<span className="text-lg text-blue-600 font-bold">+</span>
						<p className="text-sm font-medium text-blue-600">Add {getCobuyerLabel(config)}</p>
					</div>
				);
			})()}
		</div>
	);
};
